// Copyright 2017 Northern.tech AS
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// material ui
import { Alert, Button, Collapse, DialogActions, DialogContent, MenuItem, Typography, formControlClasses, textFieldClasses } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { CopyTextToClipboard } from '@northern.tech/common-ui/CopyText';
import { SettingsItem, ToggleSettingsItem } from '@northern.tech/common-ui/SettingsItem';
import { BaseDialog } from '@northern.tech/common-ui/dialogs/BaseDialog';
import { Select } from '@northern.tech/common-ui/forms/Select';
import storeActions from '@northern.tech/store/actions';
import { ALL_DEVICES, SSO_TYPES, uiPermissionsById } from '@northern.tech/store/constants';
import {
  getCurrentSession,
  getFeatures,
  getIsEnterprise,
  getIsPreview,
  getOrganization,
  getSsoConfig,
  getUserCapabilities,
  getUserRoles
} from '@northern.tech/store/selectors';
import { useAppDispatch } from '@northern.tech/store/store';
import {
  changeSsoConfig,
  deleteSsoConfig,
  downloadLicenseReport,
  getSsoConfigs,
  getTenantToken,
  getUserOrganization,
  storeSsoConfig
} from '@northern.tech/store/thunks';
import { createFileDownload, toggle } from '@northern.tech/utils/helpers';
import dayjs from 'dayjs';

import { SETTINGS_CONTENT_MAX_WIDTH, SETTINGS_INPUT_WIDTH } from '../constants';
import { SSOConfig } from './SSOConfig';

const { setSnackbar } = storeActions;

const useStyles = makeStyles()(() => ({
  tenantToken: { wordWrap: 'break-word' },
  widthLimit: {
    maxWidth: SETTINGS_CONTENT_MAX_WIDTH,
    [`.${textFieldClasses.root},.${formControlClasses.root}`]: { width: SETTINGS_INPUT_WIDTH },
    ['.settings-item-main-content']: { gridTemplateColumns: `${SETTINGS_CONTENT_MAX_WIDTH}px max-content` }
  }
}));

// unlike the ExpandableAttribute, the token should not be visible by default - thus a separate component
const TenantToken = ({ expanded, onClick, token }: { expanded: boolean; onClick: () => void; token: string }) => (
  <>
    <div className="tenant-token-text">{expanded ? token : `${token.substring(0, 8)}...`}</div>
    {!expanded && (
      <div className="clickable link-color margin-top-x-small margin-left-x-small" onClick={onClick}>
        <b>Show more</b>
      </div>
    )}
  </>
);

export const Organization = () => {
  const [hasSingleSignOn, setHasSingleSignOn] = useState(false);
  const [isConfiguringSSO, setIsConfiguringSSO] = useState(false);
  const [isResettingSSO, setIsResettingSSO] = useState(false);
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [newSso, setNewSso] = useState('');
  const [selectedSsoItem, setSelectedSsoItem] = useState(undefined);
  const isEnterprise = useSelector(getIsEnterprise);
  const { isAdmin } = useSelector(getUserRoles);
  const { groupsPermissions } = useSelector(getUserCapabilities);
  const canPreview = useSelector(getIsPreview);
  const { isHosted } = useSelector(getFeatures);
  const { id: tenantId, name: orgName, tenant_token = '' } = useSelector(getOrganization);
  const ssoConfig = useSelector(getSsoConfig);
  const dispatch = useAppDispatch();
  const { token } = useSelector(getCurrentSession);
  const { classes } = useStyles();
  const canAddDevices = groupsPermissions[ALL_DEVICES]?.includes(uiPermissionsById.manage.value);

  useEffect(() => {
    dispatch(getUserOrganization());
  }, [dispatch]);

  useEffect(() => {
    if (!canAddDevices) {
      return;
    }
    dispatch(getTenantToken());
  }, [canAddDevices, dispatch]);

  useEffect(() => {
    if (!isEnterprise) {
      return;
    }
    dispatch(getSsoConfigs());
  }, [dispatch, isEnterprise]);

  useEffect(() => {
    setHasSingleSignOn(!!ssoConfig);
    setIsConfiguringSSO(!!ssoConfig);
    if (ssoConfig) {
      setSelectedSsoItem(SSO_TYPES[ssoConfig.type]);
    }
  }, [ssoConfig]);

  const dispatchedSetSnackbar = useCallback((...args) => dispatch(setSnackbar(...args)), [dispatch]);

  const onSaveSSOSettings = useCallback(
    (id, config) => {
      const { contentType } = SSO_TYPES[selectedSsoItem.type];
      if (isResettingSSO) {
        return dispatch(deleteSsoConfig(ssoConfig)).then(() => setIsResettingSSO(false));
      }
      if (id) {
        return dispatch(changeSsoConfig({ config: { config, id }, contentType }));
      }
      return dispatch(storeSsoConfig({ config, contentType }));
    },
    [isResettingSSO, dispatch, ssoConfig, selectedSsoItem]
  );

  const onCancelSSOSettings = () => {
    setIsResettingSSO(false);
    setIsConfiguringSSO(hasSingleSignOn);
  };

  const onTokenExpansion = useCallback(() => setShowTokenWarning(true), []);

  const onDownloadReportClick = () =>
    dispatch(downloadLicenseReport())
      .unwrap()
      .then(report => createFileDownload(report, `Mender-license-report-${dayjs().format('YYYY-MM-DD')}`, token));

  const onSSOClick = () => {
    if (hasSingleSignOn) {
      setIsConfiguringSSO(false);
      return setIsResettingSSO(true);
    }
    setIsConfiguringSSO(toggle);
  };

  const onSsoSelect = useCallback(
    ({ target: { value: type = '' } }) => {
      if (ssoConfig) {
        setNewSso(type);
      } else {
        setSelectedSsoItem(SSO_TYPES[type]);
      }
    },
    [ssoConfig]
  );

  const changeSSO = () =>
    dispatch(deleteSsoConfig(ssoConfig)).then(() => {
      setSelectedSsoItem(SSO_TYPES[newSso]);
      setIsConfiguringSSO(true);
      setNewSso('');
    });

  return (
    <div className={`margin-top-small ${classes.widthLimit}`}>
      <Typography variant="h6">My organization</Typography>
      <div className="flexbox column">
        <SettingsItem title="Organization ID" secondary={tenantId} sideBarContent={<CopyTextToClipboard notify={false} token={tenantId} />} />
        <SettingsItem title="Organization name" secondary={orgName} sideBarContent={<CopyTextToClipboard notify={false} token={orgName} />} />
        {canAddDevices && (
          <SettingsItem
            classes={{ base: '', content: '', main: classes.tenantToken }}
            title="Organization token"
            description="The token is unique for your organization and ensures that only devices that you own are able to connect to your account."
            secondary={<TenantToken expanded={showTokenWarning} onClick={onTokenExpansion} token={tenant_token} />}
            sideBarContent={<CopyTextToClipboard notify={false} onCopy={onTokenExpansion} token={tenant_token} />}
            notification={
              showTokenWarning && (
                <Alert severity="warning">
                  Do not share your organization token. If leaked, unauthorized devices can send authorization requests, making it difficult to identify and
                  manage your own devices.
                </Alert>
              )
            }
          />
        )}
        {isEnterprise && isAdmin && (
          <ToggleSettingsItem
            title="Enable Single Sign-On"
            secondary={
              <div className="flexbox column">
                {isConfiguringSSO && (
                  <div>
                    <Select
                      displayEmpty
                      onChange={onSsoSelect}
                      options={Object.values(SSO_TYPES)}
                      renderOption={item => <div className="capitalized-start">{item.title}</div>}
                      selectionAttribute="type"
                      value={selectedSsoItem?.type || ''}
                      width={265}
                    >
                      <MenuItem value="">Select type</MenuItem>
                    </Select>
                  </div>
                )}
                {isResettingSSO && !isConfiguringSSO && (
                  <div className="flexbox align-items-center">
                    <Button onClick={onCancelSSOSettings}>Cancel</Button>
                    <Button onClick={onSaveSSOSettings} disabled={!hasSingleSignOn} variant="contained">
                      Save
                    </Button>
                  </div>
                )}
              </div>
            }
            onClick={onSSOClick}
            checked={!isResettingSSO && (hasSingleSignOn || isConfiguringSSO)}
          />
        )}
      </div>
      {selectedSsoItem && (
        <Collapse className="margin-top" in={isConfiguringSSO}>
          <SSOConfig
            ssoItem={selectedSsoItem}
            config={ssoConfig}
            onSave={onSaveSSOSettings}
            onCancel={onCancelSSOSettings}
            setSnackbar={dispatchedSetSnackbar}
            token={token}
          />
        </Collapse>
      )}
      {(canPreview || !isHosted) && isEnterprise && isAdmin && (
        <Button className="margin-top" onClick={onDownloadReportClick} variant="contained">
          Download license report
        </Button>
      )}
      <ChangeSsoDialog dismiss={() => setNewSso(undefined)} open={!!newSso} submit={changeSSO} />
    </div>
  );
};

const ChangeSsoDialog = ({ dismiss, open, submit }) => (
  <BaseDialog open={open} title="Change Single Sign-On type" onClose={dismiss}>
    <DialogContent style={{ overflow: 'hidden' }}>Are you sure you want to change SSO type? This will lose your current settings.</DialogContent>
    <DialogActions>
      <Button style={{ marginRight: 10 }} onClick={dismiss}>
        Cancel
      </Button>
      <Button variant="contained" color="primary" onClick={() => submit()}>
        Change
      </Button>
    </DialogActions>
  </BaseDialog>
);

export default Organization;
